/* eslint-disable consistent-return */
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  // useCallback,
} from 'react';
import { Card, CardBody, CardHeader, Spinner } from 'reactstrap';
import { useQuery, useMutation } from '@tanstack/react-query';
import Icon from '@mdi/react';
import { mdiChevronRight } from '@mdi/js';
import Tree from 'react-d3-tree';
import { toast } from 'react-toastify';
import Avatar from 'react-avatar';
import * as icons from '../../../assets';
import './OrganisationWidget.scss';
import {
  fetchDirectIndirectCount,
  fetchOrgChart,
  fetchDirectReportDetails,
  fetchIndirectReportDetails,
} from '../../../store/apis/dashboardService';
import { getAuthUser } from '../../../utils/auth.util';
import OrganisationWidgetModal from './OrganisationWidgetModal';
import { buildMiniTree } from './OrganisationWidgetUtil';
import { useViewport } from './useViewPort';
import ReporteeModal from './ReporteeModal';
import { ImageFileUtil } from '../../../utils';

const normalize = v => (typeof v === 'string' ? v.trim().toLowerCase() : '');

function OrganisationWidget() {
  const user = getAuthUser();
  const [orgModal, setOrgModal] = useState(false);
  const [translate, setTranslate] = useState({ x: 400, y: 80 });
  const [miniTreeData, setMiniTreeData] = useState([]);
  const containerRef = useRef(null);

  const { width: vw, height: vh, vvh, screenH } = useViewport();
  const approx = (val, target, tol = 24) =>
    val != null && Math.abs(val - target) <= tol;

  const W = vw || 0;
  const H = vvh || vh || 0;

  // detect “height ~1200”
  const isH1200 =
    approx(H, 1200, 24) || // viewport-ish height near 1200
    screenH === 1200; // or physical screen height is 1200

  const is16by10 = H > 0 && Math.abs(W / H - 16 / 10) < 0.06;

  const hasGrandchildren = useMemo(() => {
    if (
      !miniTreeData ||
      (Array.isArray(miniTreeData) && miniTreeData.length === 0)
    ) {
      return false;
    }
    const roots = Array.isArray(miniTreeData) ? miniTreeData : [miniTreeData];
    return roots.some(root =>
      (root?.children || []).some(ch => (ch?.children || []).length > 0),
    );
  }, [miniTreeData]);

  const scaleExtent = useMemo(() => {
    let min = 0.3;
    let max = 0.45;

    if (W < 768) {
      min = 0.35;
      max = 0.45; // mobile
    } else if (W < 1200) {
      min = 0.4;
      max = 0.45; // tablet
    } else if (W <= 1269) {
      min = 0.5;
      max = 0.6; // small desktop
    } else if (W <= 1527) {
      min = 0.32;
      max = 0.34; // FHD 150%
    } else if (W <= 1909) {
      min = 0.4;
      max = 0.5; // FHD 125%
    } else {
      min = 0.7;
      max = 0.7; // FHD 100%
    }

    // Height-aware override for 1920x1200-ish (16:10, ~1200 tall)
    if (isH1200 && is16by10 && W >= 1800) {
      min = 0.8;
      max = 0.8;
    }

    if (!hasGrandchildren) {
      min *= 1;
      max *= 1;
    }

    // safety
    min = Math.max(0.01, Math.min(min, max));
    max = Math.max(min + 0.01, Math.max(min, max));

    return { min, max };
  }, [hasGrandchildren, isH1200, is16by10, W]);

  // Set the actual zoom (since zoomable is false, this locks the scale)
  const zoom = useMemo(() => {
    if (!hasGrandchildren) {
      return scaleExtent.max;
    }
    return (scaleExtent.min + scaleExtent.max) / 2;
  }, [hasGrandchildren, scaleExtent]);

  const { data: fetchedChart, isLoading: isChartLoading } = useQuery({
    queryKey: ['fetchedChart'],
    queryFn: fetchOrgChart,
  });

  const { data: fetchedCounts, isLoading: isCountLoading } = useQuery({
    queryKey: ['fetchedCounts'],
    queryFn: fetchDirectIndirectCount,
  });

  useEffect(() => {
    if (!fetchedChart || !user?.employeeId) return;

    const tree = buildMiniTree(fetchedChart, user.employeeId);
    setMiniTreeData(tree);

    const centerTree = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const { offsetWidth, offsetHeight } = container;

      setTranslate({
        x: offsetWidth / 2,
        y: offsetHeight / 6,
      });

      container.scrollLeft =
        (container.scrollWidth - container.clientWidth) / 2;
      container.scrollTop = 0;
    };

    const waitForContainer = () => {
      if (containerRef.current) {
        centerTree();
      } else {
        requestAnimationFrame(waitForContainer);
      }
    };

    waitForContainer();
    window.addEventListener('resize', centerTree);

    return function cleanup() {
      window.removeEventListener('resize', centerTree);
    };
  }, [fetchedChart, user?.employeeId]);

  const toggleOrgModal = () => {
    setOrgModal(!orgModal);
  };

  const getImage = item => {
    if (item) {
      return ImageFileUtil.getEmployeeFilePath(item);
    }
    return null;
  };

  const userEmpId = normalize(user?.employeeId);

  const renderNode = ({ nodeDatum, toggleNode }) => {
    const isUserNode = normalize(nodeDatum?.attributes?.Code) === userEmpId;

    const isExtraNode = nodeDatum?.isExtra;

    if (isExtraNode) {
      return (
        <foreignObject width={80} height={80} x={-40} y={-20}>
          <div
            className="org-card-node-extra cursor-pointer"
            onClick={toggleOrgModal}
          >
            {nodeDatum.name}
          </div>
        </foreignObject>
      );
    }

    return (
      <foreignObject
        width="clamp(250px, 20vw, 350px)"
        height="clamp(100px, 11vh, 200px)"
        x="calc(clamp(250px, 20vw, 350px) / -2)"
        y="calc(clamp(100px, 11vh, 200px) / -2)"
      >
        <div
          className="org-card-node"
          style={{ backgroundColor: isUserNode ? '#017EBD' : undefined }}
          onClick={toggleNode}
        >
          <div className="d-flex flex-row align-items-center gap-3 p-1">
            <div>
              <Avatar
                size="50px"
                name={nodeDatum?.name || 'Unknown'}
                color="#CFCBDC"
                fgColor="#475467"
                maxInitials={2}
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                }}
                src={getImage(nodeDatum?.attributes?.Imagefile)}
                round
              />
            </div>
            <div className="d-flex flex-column">
              <div
                className="fw-semibold fs-5"
                style={{
                  color: isUserNode ? '#fff' : '#1E7DB2',
                }}
              >
                {nodeDatum?.name}
              </div>
              <div
                className=""
                style={{
                  color: isUserNode ? '#fff' : '#000',
                }}
              >
                {nodeDatum.attributes.Title}
              </div>
            </div>
          </div>
        </div>
      </foreignObject>
    );
  };

  const [reporteeData, setReporteeData] = useState([]);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const toggleReportModal = () => {
    setReportModalOpen(!reportModalOpen);
  };

  const { mutateAsync: fetchDirectReporteeDetails } = useMutation({
    mutationFn: fetchDirectReportDetails,
    onError: err =>
      toast.error(err?.message || 'Failed to fetch Direct Report Details'),
  });

  const { mutateAsync: fetchIndirectReporteeDetails } = useMutation({
    mutationFn: fetchIndirectReportDetails,
    onError: err =>
      toast.error(err?.message || 'Failed to fetch Indirect Report Details'),
  });

  const handleReportModal = async type => {
    try {
      if (type === 'Direct') {
        const response = await fetchDirectReporteeDetails({
          empId: user?.employeeId,
        });
        setReporteeData({
          reportType: 'Direct',
          data: Array.isArray(response) ? response : [],
        });
      } else {
        const response = await fetchIndirectReporteeDetails({
          empId: user?.employeeId,
        });
        setReporteeData({
          reportType: 'Indirect',
          data: Array.isArray(response) ? response : [],
        });
      }
      toggleReportModal();
    } catch (err) {
      toast.error(err?.message || `Failed to fetch ${type} Report Details`);
    }
  };

  const renderNodeSize = () => {
    if (isH1200 && is16by10 && W >= 1800) {
      return { x: 270, y: 170 };
    }

    if (hasGrandchildren) {
      return { x: 270, y: 150 };
    }

    return { x: 270, y: 160 };
  };

  return (
    <Card className="organization-widget org-card">
      <CardHeader className="org-card-header">
        <div className="d-flex flex-row align-items-center justify-content-between gap-2 pb-0">
          <div className="d-flex flex-row align-items-center gap-2">
            <img src={icons.iconsDashboardOrg} alt="Org Icon" height="20px" />
            <span className="org-main-heading fs-5">Organization Chart</span>
          </div>
          <div className="cursor-pointer">
            <Icon
              path={mdiChevronRight}
              size={1}
              color="#017EBD"
              onClick={toggleOrgModal}
            />
          </div>
        </div>
        <div className="org-border-bottom" />
      </CardHeader>
      <CardBody className="p-0 d-flex flex-column gap-1">
        {isChartLoading || isCountLoading ? (
          <div className="d-flex align-items-center justify-content-center p-3 flex-grow-1">
            <Spinner color="dark" size="sm" />
            <span className="ms-2">Loading...</span>
          </div>
        ) : (
          <div
            className="org-tree p-1 flex-grow-1"
            ref={containerRef}
            onClick={toggleOrgModal}
          >
            {miniTreeData.length > 0 && miniTreeData ? (
              <Tree
                data={miniTreeData}
                orientation="vertical"
                translate={translate}
                scaleExtent={scaleExtent}
                nodeSize={renderNodeSize()}
                renderCustomNodeElement={renderNode}
                draggable={false}
                zoomable={false}
                pathFunc="step"
                collapsible={false}
                pathClassFunc={() => 'custom-link'}
                zoom={zoom}
              />
            ) : (
              <div className="text-center py-5">
                <span className="text-muted">No data available.</span>
              </div>
            )}
          </div>
        )}
        <div className="org-count d-flex flex-row justify-content-between gap-3 align-items-center px-3">
          <div
            className="org-request-wrapper w-50 cursor-pointer"
            onClick={() => handleReportModal('Direct')}
          >
            <span className="fw-semibold fs-3">
              {fetchedCounts?.[0]?.Direct_Reports ?? 'N/A'}
            </span>
            <div className="d-flex flex-row align-items-center justify-content-between w-100 pe-3">
              <span
                className="fs-6 text-nowrap"
                style={{ color: '#5b6879' }}
                title="Number of employees who report directly to you."
              >
                Direct Reports
              </span>
              <Icon path={mdiChevronRight} size={1} color="#0DA6EA" />
            </div>
          </div>
          <div
            className="org-request-wrapper w-50 cursor-pointer"
            onClick={() => handleReportModal('Indirect')}
          >
            <span className="fw-semibold fs-3">
              {fetchedCounts?.[0]?.InDirect_Reports ?? 'N/A'}
            </span>
            <div className="d-flex flex-row align-items-center justify-content-between w-100 pe-3">
              <span
                className="fs-6 text-nowrap"
                title="Number of employees reporting to your direct reportees."
                style={{ color: '#5b6879' }}
              >
                Indirect Reports
              </span>
              <Icon path={mdiChevronRight} size={1} color="#0DA6EA" />
            </div>
          </div>
        </div>
      </CardBody>
      {orgModal && (
        <OrganisationWidgetModal
          isOpen={orgModal}
          toggle={toggleOrgModal}
          chart={fetchedChart}
          user={userEmpId}
        />
      )}
      <ReporteeModal
        isOpen={reportModalOpen}
        toggle={toggleReportModal}
        data={reporteeData?.data}
        header={reporteeData?.reportType}
      />
    </Card>
  );
}

export default OrganisationWidget;
